import React, { useEffect, useRef } from "react";
import { cleanTextForSpeech } from "./speechText";

export interface ReadableItem {
  key: string;
  texts: string[];
  onTextStart?: (index: number) => void;
  onTextDone?: (index: number) => void;
}

interface VoiceControls {
  speed: number;
  voice: string;
  isReading: boolean;
  isPaused: boolean;
  pitch: number;
  align: boolean;
}

const VOICE_CONTROLS_CACHE_KEY = "voiceControls_cache";

// iOS kills speechSynthesis internally after ~15 s of speaking.
// Pinging every 8 s gives a comfortable margin on all iOS versions.
const IOS_KEEPALIVE_INTERVAL_MS = 8_000;

function getDefaultVoiceControls(): VoiceControls {
  return {
    speed: 1.2,
    voice: "Google UK English Male",
    isReading: false,
    isPaused: false,
    pitch: 1,
    align: false,
  };
}

function loadVoiceControlsFromCache(): VoiceControls {
  try {
    const cached = localStorage.getItem(VOICE_CONTROLS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Merge with defaults to handle any missing properties
      return { ...getDefaultVoiceControls(), ...parsed };
    }
  } catch (_) {
    // If cache is corrupted, fall back to defaults
  }
  return getDefaultVoiceControls();
}

export function useReadAloud(deps: React.DependencyList = []) {
  const [currentlyReadingKey, setCurrentlyReadingKey] =
    React.useState<string>("");
  const [currentlyReadingTextIndex, setCurrentlyReadingTextIndex] =
    React.useState<number>(-1);

  const skipTextRef = useRef<(() => void) | null>(null);

  const [voiceControls, setVoiceControls] = React.useState<VoiceControls>(
    loadVoiceControlsFromCache()
  );

  // Save voiceControls to cache whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(VOICE_CONTROLS_CACHE_KEY, JSON.stringify(voiceControls));
    } catch (_) {
      // Silently fail if localStorage is unavailable
    }
  }, [voiceControls]);
  
  useEffect(() => {
    if (!voiceControls.align) return;

    setTimeout(() => {
      const element = document.getElementById(currentlyReadingKey);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 1);
  }, [currentlyReadingKey, voiceControls.align, ...deps]);

  async function speakItems(
    items: ReadableItem[],
    onItemDone?: (key: string) => void,
  ) {
    for (const item of items) {
      if (
        currentlyReadingKey &&
        currentlyReadingKey === item.key &&
        voiceControls.isReading === true
      ) {
        continue;
      }
      setCurrentlyReadingKey(item.key);

      if (voiceControls.isPaused) return;

      for (let textIndex = 0; textIndex < item.texts.length; textIndex++) {
        setCurrentlyReadingTextIndex(textIndex);
        item.onTextStart?.(textIndex);

        // Safety net: strip HTML tags/entities, URLs and emoji before speaking,
        // regardless of how the caller built texts[]. Nothing to say -> skip.
        const spoken = cleanTextForSpeech(item.texts[textIndex]);
        if (!spoken) {
          item.onTextDone?.(textIndex);
          continue;
        }

        const utterance = new SpeechSynthesisUtterance(spoken);
        utterance.voice =
          speechSynthesis
            .getVoices()
            .find((voice) => voice.name === voiceControls.voice) || null;
        utterance.rate = voiceControls.speed;
        utterance.pitch = voiceControls.pitch;

        const promise = new Promise<void>((resolve) => {
          utterance.onend = () => resolve();
          skipTextRef.current = resolve;
          speechSynthesis.speak(utterance);
        });

        await promise;
        skipTextRef.current = null;
        item.onTextDone?.(textIndex);
      }

      setCurrentlyReadingTextIndex(-1);
      onItemDone?.(item.key);
    }
  }

  function skipItem(
    _items: ReadableItem[],
    _onItemDone?: (key: string) => void,
  ) {
    speechSynthesis.cancel();
    if (skipTextRef.current) {
      skipTextRef.current();
      skipTextRef.current = null;
    }
  }

  function toggleReading(
    items: ReadableItem[],
    onItemDone?: (key: string) => void,
  ) {
    if (voiceControls.isReading) {
      speechSynthesis.cancel();
      setVoiceControls((prev) => ({
        ...prev,
        isReading: false,
        isPaused: false,
      }));
    } else {
      speakItems(items, onItemDone);
      setVoiceControls((prev) => ({
        ...prev,
        isReading: true,
      }));
    }
  }

  return {
    voiceControls,
    setVoiceControls,
    currentlyReadingKey,
    currentlyReadingTextIndex,
    toggleReading,
    skipItem,
  };
}
